import React, { useMemo, useEffect } from 'react';

import {
  Flex, GridItem, HStack, Stack, Tab, TabList, Tabs, Text,
} from '@chakra-ui/react';
import { useSpring, animated } from 'react-spring';
import { useStateList } from 'react-use';
import {
  PieChart, Pie, ResponsiveContainer, Legend, Tooltip,
} from 'recharts';

import colors from 'theme/colors';
import { handleObjectString } from 'utils/parsers';

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({
  cx, cy, midAngle, innerRadius, outerRadius, percent, fill,
}) => {
  // Favor evitar mexer e nem perguntar o porque foi feito assim
  // Para futuras referências suba o contador
  // para quantas vezes foi necessário manutenção nesse trecho
  // Manutenções: 1
  const radius = innerRadius + (outerRadius - innerRadius - 16) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * (RADIAN));
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 4;

  const textAnchor = cos >= 0 ? 'start' : 'end';
  if (percent > 0.2) {
    return (
      <Text
        as="text"
        x={x}
        y={y}
        fill="white.default"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </Text>
    );
  }
  return (
    <>
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${my}`}
        stroke={fill}
        fill="none"
      />
      <Text
        as="text"
        variant="mediumBold"
        x={ex + (cos >= 0 ? 1 : -1)}
        y={my + 5}
        textAnchor={textAnchor}
        fill={fill}>
        {`${(percent * 100).toFixed(0)}%`}
      </Text>
    </>
  );
};

const keys = ['routes', 'activities.collect', 'activities.delivery', 'activities.dispatch'];

function Label({ color, value, payload }) {
  const [style, inc] = useSpring(() => ({
    x: 0,
  }));

  useEffect(() => {
    inc({ x: payload.value });
  }, [payload]);
  const AnimatedText = animated(Text);

  return (
    <HStack>
      <Flex
        bg={color}
        borderRadius={100}
        w={3}
        h={3}
      />
      <AnimatedText
        variant="xSmallSemiBold"
        color="gray.500">
        {style.x.to((x) => `${value}: ${x.toFixed(0)}`)}
      </AnimatedText>
    </HStack>
  );
}

function SumLabel({ payload }) {
  const [style, inc] = useSpring(() => ({
    x: 0,
  }));

  useEffect(() => {
    inc({ x: payload.reduce((acc, next) => (acc + next.payload.value), 0) });
  }, [payload]);
  const AnimatedText = animated(Text);

  return (
    <AnimatedText
      variant="xSmallBold"
      color="gray.500">
      {style.x.to((x) => `Total: ${x.toFixed(0)}`)}
    </AnimatedText>
  );
}

function CustomLegend({ payload }) {
  return (
    <Stack>
      {payload.map(Label)}
      <SumLabel payload={payload} />
    </Stack>
  );
}

export default function StatusCard({ data = {} }) {
  const { state: tab, setStateAt: setTabAt, currentIndex: tabIndex } = useStateList(keys);
  const graph = useMemo(() => {
    const { ongoing, overdue, finished } = handleObjectString(data, tab) ?? {};
    return [
      { name: 'Em andamento', value: ongoing ?? 0, fill: colors.system.ongoing.default },
      { name: 'Atrasadas', value: overdue ?? 0, fill: colors.system.error.default },
      { name: 'Encerradas', value: finished ?? 0, fill: colors.system.success.default },
    ];
  }, [data, tab]);

  return (
    <GridItem
      as={Flex}
      bg="white.default"
      color="gray.50"
      flexDir="column"
      borderRadius={8}
      colSpan={3}>
      <Flex
        px={2}
        pt={1}
        borderBottom="1px">
        <Tabs
          index={tabIndex}
          variant="line"
          onChange={setTabAt}>
          <TabList
            borderBottom="none">
            <Tab>
              <Text
                color="gray.500"
                variant="xSmallBold">
                Rotas
              </Text>
            </Tab>
            <Tab>
              <Text
                color="gray.500"
                variant="xSmallBold">
                Coletas
              </Text>
            </Tab>
            <Tab>
              <Text
                color="gray.500"
                variant="xSmallBold">
                Entregas
              </Text>
            </Tab>
            <Tab>
              <Text
                color="gray.500"
                variant="xSmallBold">
                Despachos
              </Text>
            </Tab>
          </TabList>
        </Tabs>
      </Flex>
      <Flex
        width="100%"
        p={4}>
        <Flex
          flexDir="column"
          width="100%">
          <ResponsiveContainer
            width="100%"
            height={200}>
            <PieChart>
              <Pie
                data={graph}
                label={renderCustomizedLabel}
                labelLine={false}
                legendType="circle"
                dataKey="value"
                nameKey="name"
                outerRadius={70}
              />
              <Tooltip />
              <Legend
                width={150}
                layout="vertical"
                verticalAlign="middle"
                align="right"
                content={CustomLegend}
              />
            </PieChart>
          </ResponsiveContainer>
        </Flex>
      </Flex>
    </GridItem>
  );
}
